import { BaseFormModeAdapter } from "./adapters";
import {
  FormConfiguration,
  SubmissionContext,
  FormInitParams,
} from "./definitions";
import {
  transformScanResultToFormData,
  buildDraftFormResetData,
} from "../util";

/**
 * Adapter for Draft mode (non-wizard)
 * Handles form behavior for processing scan results into Gaia objects
 * This is used for direct draft processing without wizard steps or assessment
 */
export class DraftAdapter extends BaseFormModeAdapter {
  /**
   * Initialize form data for draft mode
   */
  initializeFormData(params: FormInitParams): void {
    const {
      reset,
      scanResultData,
      scanJobData,
      customAttributes,
      uiContent,
      currentUser,
    } = params;

    if (scanResultData && customAttributes) {
      const ownerValue = scanJobData?.scanJob?.createdBy || currentUser?.alias;
      const scanFormData = transformScanResultToFormData(
        scanResultData.scanResult,
        customAttributes,
        uiContent,
        ownerValue,
      );
      reset(buildDraftFormResetData(scanFormData));
    }
  }

  /**
   * Get form configuration for draft mode
   */
  getFormConfig(): FormConfiguration {
    return {
      ...super.getFormConfig(),
      // Draft mode specific overrides
      showTemplates: false,
      showFileUpload: false,
    };
  }

  shouldLoadScanResult(): boolean {
    return true;
  }

  shouldLoadScanJob(): boolean {
    return true;
  }

  /**
   * Handle draft submission
   * Flow: transform data → call createGaiaObject hook → process draft operations
   */
  async handleSubmit(data: any, context: SubmissionContext): Promise<void> {
    if (context.ui.setDisableCreateEdit) {
      context.ui.setDisableCreateEdit(true);
    }
    // 1. Transform data (using component function)
    const processedData = context.transforms.processGaiaObjectData(data);

    // 2. Create GaiaObject (hook handles API + notification + navigation)
    const response = await context.api.createGaiaObject(processedData, context);

    // 3. Process draft operations (register scan doc + update scan status)
    await context.api.processDraftOperations(response.shortId, context);
  }
}
